/**
 * Design tokens compartilhados por web e mobile.
 *
 * Estetica: editorial, escura por padrao, com acento quente (ambar).
 *
 * A primeira versao usava verde e vermelho dessaturados, com o argumento de
 * que dinheiro precisa de hierarquia e nao de semaforo. O argumento e bom
 * para um relatorio impresso e errado para esta tela: aqui o sinal do numero
 * E a informacao mais consultada, e um vermelho timido obriga a LER o valor
 * para saber se ele e negativo. As cores de entrada e saida subiram de
 * saturacao ate serem reconheciveis de relance, que e o unico uso real
 * delas. O resto da interface continua sobrio, para que sobrar cor
 * signifique alguma coisa.
 *
 * Contraste: todos os pares texto/fundo abaixo foram escolhidos para passar
 * AA (4.5:1 em texto normal, 3:1 em texto grande e bordas de foco).
 */
export const palette = {
    // Neutros frios e profundos: o fundo nao e preto puro, que cansa a vista
    // e apaga a elevacao dos cartoes.
    ink: {
        950: '#0b0d10',
        900: '#101318',
        850: '#151922',
        800: '#1b2028',
        700: '#262c37',
        600: '#353d4a',
        500: '#4c5666',
        400: '#6b7789',
        300: '#8f9aab',
        200: '#b9c1cd',
        100: '#dee3ea',
        50: '#f4f6f9',
        0: '#ffffff',
    },
    amber: {
        600: '#9a6210',
        500: '#c9841a',
        400: '#eda52a',
        300: '#ffc247',
        200: '#ffd782',
    },
    green: {
        600: '#12764e',
        500: '#159c64',
        400: '#1fc47e',
        300: '#4ee3a4',
    },
    red: {
        600: '#a32424',
        500: '#d13b3b',
        400: '#f25c5c',
        300: '#ff8080',
    },
    blue: {
        600: '#1f5aa0',
        500: '#2f7fd4',
        400: '#57a0ef',
    },
    violet: {
        600: '#5b32b5',
        500: '#7a4fdc',
        400: '#a186f5',
    },
    teal: {
        600: '#0d7676',
        500: '#12a0a0',
        400: '#22c9c9',
    },
    rose: {
        600: '#ad2764',
        500: '#dc3d85',
        400: '#f56ba8',
    },
};
export const darkCategoryColors = {
    ink: { solid: palette.ink[300], soft: 'rgba(143, 154, 171, 0.18)' },
    amber: { solid: palette.amber[300], soft: 'rgba(255, 194, 71, 0.20)' },
    green: { solid: palette.green[300], soft: 'rgba(78, 227, 164, 0.18)' },
    red: { solid: palette.red[300], soft: 'rgba(255, 128, 128, 0.18)' },
    blue: { solid: palette.blue[400], soft: 'rgba(87, 160, 239, 0.20)' },
    violet: { solid: palette.violet[400], soft: 'rgba(161, 134, 245, 0.20)' },
    teal: { solid: palette.teal[400], soft: 'rgba(34, 201, 201, 0.20)' },
    rose: { solid: palette.rose[400], soft: 'rgba(245, 107, 168, 0.20)' },
};
export const lightCategoryColors = {
    ink: { solid: palette.ink[500], soft: 'rgba(76, 86, 102, 0.12)' },
    amber: { solid: palette.amber[600], soft: 'rgba(154, 98, 16, 0.14)' },
    green: { solid: palette.green[600], soft: 'rgba(18, 118, 78, 0.14)' },
    red: { solid: palette.red[600], soft: 'rgba(163, 36, 36, 0.14)' },
    blue: { solid: palette.blue[600], soft: 'rgba(31, 90, 160, 0.14)' },
    violet: { solid: palette.violet[600], soft: 'rgba(91, 50, 181, 0.14)' },
    teal: { solid: palette.teal[600], soft: 'rgba(13, 118, 118, 0.14)' },
    rose: { solid: palette.rose[600], soft: 'rgba(173, 39, 100, 0.14)' },
};
export const darkColors = {
    bg: palette.ink[950],
    bgElevated: palette.ink[850],
    bgSunken: '#07090b',
    surface: palette.ink[900],
    surfaceHover: palette.ink[800],
    border: palette.ink[700],
    borderStrong: palette.ink[600],
    text: palette.ink[100],
    textMuted: palette.ink[300],
    textSubtle: palette.ink[400],
    textInverted: palette.ink[950],
    accent: palette.amber[300],
    accentHover: palette.amber[200],
    accentText: palette.ink[950],
    income: palette.green[300],
    expense: palette.red[300],
    neutralFlow: palette.ink[300],
    danger: palette.red[400],
    dangerText: palette.ink[0],
    success: palette.green[400],
    warning: palette.amber[400],
    focus: palette.amber[300],
    overlay: 'rgba(7, 9, 11, 0.72)',
};
export const lightColors = {
    bg: palette.ink[50],
    bgElevated: palette.ink[0],
    bgSunken: '#e9edf3',
    surface: palette.ink[0],
    surfaceHover: palette.ink[50],
    border: '#dbe1e9',
    borderStrong: palette.ink[200],
    text: palette.ink[900],
    textMuted: palette.ink[500],
    textSubtle: palette.ink[400],
    textInverted: palette.ink[0],
    accent: palette.amber[600],
    accentHover: palette.amber[500],
    accentText: palette.ink[0],
    income: palette.green[600],
    expense: palette.red[600],
    neutralFlow: palette.ink[500],
    danger: palette.red[600],
    dangerText: palette.ink[0],
    success: palette.green[600],
    warning: palette.amber[600],
    focus: palette.amber[600],
    overlay: 'rgba(11, 13, 16, 0.45)',
};
/** Escala 4pt. */
export const space = {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
};
export const radius = {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '999px',
};
export const typography = {
    fontSans: "'Inter var', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSerif: "'Newsreader', 'Iowan Old Style', Georgia, 'Times New Roman', serif",
    fontMono: "'JetBrains Mono', 'SF Mono', 'Cascadia Mono', Consolas, monospace",
    /**
     * A escala subiu um degrau inteiro.
     *
     * O corpo era 15px e a letra miuda 12px — tamanho de interface de
     * desenvolvedor, nao de sistema que alguem le todo dia conferindo numero.
     * Valor em tabela financeira precisa ser lido sem esforco, e 12px num
     * monitor grande e onde o erro de leitura nasce.
     */
    size: {
        xs: '13px',
        sm: '15px',
        base: '17px',
        md: '19px',
        lg: '23px',
        xl: '29px',
        '2xl': '38px',
        '3xl': '48px',
    },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    leading: { tight: 1.2, snug: 1.35, normal: 1.55, relaxed: 1.7 },
    tracking: { tight: '-0.015em', normal: '0', wide: '0.06em' },
};
export const shadow = {
    sm: '0 1px 2px rgba(0,0,0,0.30)',
    md: '0 4px 14px rgba(0,0,0,0.35)',
    lg: '0 16px 40px rgba(0,0,0,0.45)',
};
export const motion = {
    fast: '120ms',
    base: '200ms',
    slow: '320ms',
    easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
};
export const zIndex = { base: 0, dropdown: 100, sticky: 200, overlay: 300, modal: 400, toast: 500 };
export const breakpoints = { sm: '480px', md: '768px', lg: '1024px', xl: '1280px' };
/** Gera as custom properties CSS de um tema. */
export function colorsToCssVars(colors) {
    return Object.entries(colors)
        .map(([key, value]) => `  --color-${kebab(key)}: ${value};`)
        .join('\n');
}
function kebab(value) {
    return value.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
export const tokens = {
    palette,
    darkColors,
    lightColors,
    space,
    radius,
    typography,
    shadow,
    motion,
    zIndex,
    breakpoints,
};
//# sourceMappingURL=index.js.map